<script setup lang="ts">
import type { AddressForm, AddressSearchResult, CustomerDto } from '@/types';
import { computed, ref, watch } from 'vue';
import InputCardComponent from '@/components/InputCardComponent.vue';
import { useCustomerStore } from '@/stores/customer';
import { useMessageStore } from '@/stores/messageStore';
import { postnumre } from '@/data/postnumre';

// address1, zipCode and city are set here, the rest of the customer is set by ContactInfoComponent
const customerForm = defineModel<Partial<CustomerDto>>('customerForm', { required: true })
const addressForm = defineModel<AddressForm>('addressForm', { required: true })

const customerStore = useCustomerStore()
const message = useMessageStore()

// Search by number or city, e.g. "4000" or "Roskilde". The postnummer is what the rest of the form uses
const postnummerOptions = postnumre.map(x => ({ title: `${x.nr} ${x.navn}`, value: x.nr, navn: x.navn.toLowerCase() }))

// Values are what the address API expects. Per the address rules (adressebekendtgørelsen) "kl" is the basement
// right under the ground floor and k2, k3, k4 are the ones below it
const etageOptions = [
  { title: 'kl 4', value: 'k4' },
  { title: 'kl 3', value: 'k3' },
  { title: 'kl 2', value: 'k2' },
  { title: 'kl 1', value: 'kl' },
  { title: 'st.', value: 'st' },
  ...Array.from({ length: 100 }, (_, i) => ({ title: `${i + 1}.`, value: `${i + 1}` }))
]
const doerOptions = ['tv', 'mf', 'th']

const postnummerSearch = ref('')
const addressOptions = ref<AddressSearchResult[]>([])
const searching = ref(false)
const searched = ref(false)
const rules = ref({
  required: (value: unknown) => !!value || "Påkrævet",
  zipCode: (value: string) =>
    /^[\d]{4}$/.test(value) ||
    "Må kun indeholde tal og skal være 4 cifre.",
  vejnavn: (value: string) =>
    (!!value && value.length <= 40) ||
    "Må maksimalt være 40 tegn.",
  husnummer: (value?: string) =>
    !value || /^[\d]{1,3}[a-zA-Z]?$/.test(value) ||
    "Skal være et tal, evt. efterfulgt af et bogstav (f.eks. 239B).",
  doer: (value?: string | null) =>
    !value || /^(tv|mf|th|[\d]{1,4})$/i.test(value) ||
    "Skal være tv, mf, th eller et tal.",
})

// Empty until the user types, so the whole list is never shown. The picked one is kept so the field can show it
const postnummerItems = computed(() => {
  const search = postnummerSearch.value?.trim().toLowerCase()
  const selected = postnummerOptions.filter(x => x.value === customerForm.value.zipCode)

  if (!search || search === selected[0]?.title.toLowerCase()) {
    return selected
  }

  // Number from the start ("40" -> 4000, not 1040), city anywhere in the name
  return postnummerOptions.filter(x =>
    x.value.startsWith(search) ||
    x.title.toLowerCase().startsWith(search) ||
    x.navn.includes(search)
  )
})

// Postnummer first, then vejnavn. Only then can the rest be filled and the address API be called
const zipCodeValid = computed(() => rules.value.zipCode(customerForm.value.zipCode ?? '') === true)
const streetValid = computed(() => zipCodeValid.value && rules.value.vejnavn(addressForm.value.vejnavn ?? '') === true)
const canSearch = computed(() =>
  streetValid.value &&
  rules.value.husnummer(addressForm.value.husnummer) === true &&
  rules.value.doer(addressForm.value.doer) === true
)

let searchTimeout: ReturnType<typeof setTimeout> | undefined
let lastSearch = 0

async function searchAddresses() {
  if (!canSearch.value) {
    addressOptions.value = []
    searched.value = false
    return
  }

  const { vejnavn, husnummer, etage, doer } = addressForm.value
  const search = ++lastSearch

  searching.value = true

  try {
    const result = await customerStore.searchAddresses({
      postnummer: customerForm.value.zipCode as string,
      vejnavn: vejnavn as string,
      husnummer: husnummer || undefined,
      etage: etage || undefined,
      doer: doer || undefined
    })

    // Ignore answers to older searches that arrive after a newer one
    if (search === lastSearch) {
      addressOptions.value = result
      searched.value = true
    }
  } catch (error) {
    console.error(error)

    if (search === lastSearch) {
      message.addError('Der skete en fejl ved at hente adresser')
    }
  } finally {
    if (search === lastSearch) {
      searching.value = false
    }
  }
}

// Called when the user changes a search field, so a picked address can't go stale
function clearSelectedAddress() {
  addressForm.value.selected = undefined
  customerForm.value.address1 = ''
  customerForm.value.city = ''
}

function onSelectAddress(option: AddressSearchResult) {
  // A street, when what was typed matches more than one ("lundeh" -> Lundehusene, Lundemosen). Search that street instead
  if (option.type === 'navngivenvejpostnummer') {
    clearSelectedAddress()
    addressForm.value.vejnavn = option.vejnavn
    return
  }

  // A house number with more than one address (floors/doors), show its addresses instead
  if (option.type === 'husnummer') {
    clearSelectedAddress()
    addressForm.value.husnummer = option.husnummer
    return
  }

  addressForm.value.selected = option
  customerForm.value.address1 = option.adresselinje
  // What is left after the adresselinje is ", postnummer city"
  customerForm.value.city = option.titel.slice(option.adresselinje.length).replace(/^,?\s*\d{4}\s*/, '')
}

watch(() => [
  customerForm.value.zipCode,
  addressForm.value.vejnavn,
  addressForm.value.husnummer,
  addressForm.value.etage,
  addressForm.value.doer
], () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(searchAddresses, 300)
})
</script>

<template>
  <InputCardComponent label="Adresse">
    <v-autocomplete
      v-model="customerForm.zipCode"
      v-model:search="postnummerSearch"
      :items="postnummerItems"
      label="Postnummer eller by"
      name="zipCode"
      no-filter
      :hide-no-data="!postnummerSearch"
      no-data-text="Ingen postnumre fundet"
      :rules="[rules.required]"
      auto-select-first
      clearable
      @update:model-value="clearSelectedAddress"
    />
    <v-text-field
      v-model="addressForm.vejnavn"
      label="Vejnavn"
      name="street"
      type="text"
      maxlength="40"
      counter="40"
      :disabled="!zipCodeValid"
      :rules="[rules.required, rules.vejnavn]"
      clearable
      @update:model-value="clearSelectedAddress"
    />
    <v-text-field
      v-model="addressForm.husnummer"
      label="Husnummer"
      name="houseNumber"
      type="text"
      maxlength="4"
      :disabled="!streetValid"
      :rules="[rules.husnummer]"
      clearable
      @update:model-value="clearSelectedAddress"
    />
    <v-row dense>
      <v-col cols="6">
        <v-select
          v-model="addressForm.etage"
          :items="etageOptions"
          label="Etage (valgfri)"
          :disabled="!streetValid"
          clearable
          @update:model-value="clearSelectedAddress"
        />
      </v-col>
      <v-col cols="6">
        <v-combobox
          v-model="addressForm.doer"
          :items="doerOptions"
          label="Dør (valgfri)"
          maxlength="4"
          :disabled="!streetValid"
          :rules="[rules.doer]"
          clearable
          @update:model-value="clearSelectedAddress"
        />
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="searching"
      color="primary"
      indeterminate
    />

    <template v-if="canSearch && searched && !searching">
      <v-list
        v-if="addressOptions.length"
        class="border rounded"
        density="compact"
        max-height="320"
      >
        <v-list-subheader>Vælg din adresse</v-list-subheader>
        <v-list-item
          v-for="option in addressOptions"
          :key="option.id"
          :title="option.titel"
          :active="addressForm.selected?.id === option.id"
          color="primary"
          @click="onSelectAddress(option)"
        >
          <template #append>
            <v-icon v-if="addressForm.selected?.id === option.id">mdi-check</v-icon>
            <!-- A street or a house number with several addresses: picking it narrows the search -->
            <v-icon v-else-if="option.type !== 'adresse'">mdi-chevron-right</v-icon>
          </template>
        </v-list-item>
      </v-list>
      <!-- The API sends at most 100 -->
      <div
        v-if="addressOptions.length >= 100"
        class="text-caption mt-2"
      >
        Viser de første 100 adresser. Skriv husnummer for at finde din.
      </div>
      <v-alert
        v-if="!addressOptions.length"
        type="info"
        variant="tonal"
        density="compact"
        text="Vi kunne ikke finde adressen. Tjek postnummer og vejnavn."
      />
    </template>
  </InputCardComponent>
</template>
